import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Loader2, UserCircle, Check, Star, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

export function ProfileSwitcherModal({ isOpen, onClose }) {
  const { user, getProfiles, switchProfile } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
    }
  }, [isOpen]);

  const fetchProfiles = async () => {
    setLoading(true);
    const result = await getProfiles();
    if (result.success) {
      setProfiles(result.profiles);
    } else {
      toast.error("Failed to load profiles");
    }
    setLoading(false);
  };

  const handleSwitch = async (profileId) => {
    if (switching) return;
    setSwitching(profileId);
    const result = await switchProfile(profileId);
    if (result.success) {
      toast.success("Profile switched!");
      onClose();
      window.location.reload();
    } else {
      toast.error(result.error || "Failed to switch profile");
    }
    setSwitching(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="profile-switcher-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Switch Profile</h2>
              <p className="text-white/70 text-xs">{user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/20 transition-colors" data-testid="profile-switcher-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Profiles */}
        <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
          ) : profiles.length > 0 ? (
            profiles.map((profile) => {
              const isCurrent = profile.is_current;
              return (
                <button
                  key={profile.id}
                  onClick={() => !isCurrent && handleSwitch(profile.id)}
                  disabled={isCurrent || switching === profile.id}
                  data-testid={`profile-option-${profile.id}`}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left",
                    isCurrent
                      ? "bg-indigo-500/20 border-2 border-indigo-400"
                      : "bg-slate-800 border border-slate-700 hover:border-indigo-400/50 hover:bg-slate-700/50 cursor-pointer"
                  )}
                >
                  {/* Avatar */}
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0",
                    isCurrent
                      ? "bg-gradient-to-br from-indigo-500 to-purple-500 text-white"
                      : "bg-slate-700 text-slate-300"
                  )}>
                    {(profile.player_name || "?")[0].toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={cn("font-semibold text-sm truncate", isCurrent ? "text-indigo-300" : "text-white")}>
                      {profile.player_name || "Unnamed Profile"}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                      <span>Level {profile.level}</span>
                      <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-yellow-500" />{profile.total_xp?.toLocaleString() || 0} XP</span>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="shrink-0">
                    {isCurrent ? (
                      <div className="flex items-center gap-1 text-indigo-400 text-xs font-medium">
                        <Check className="w-4 h-4" /> Active
                      </div>
                    ) : switching === profile.id ? (
                      <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                </button>
              );
            })
          ) : (
            <p className="text-center text-slate-500 py-8">No profiles found</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileSwitcherModal;
